// Shared desktop styles - single source of truth
// Accent colors are passed per-component, structure is identical

export const outerContainerStyle = {
  minHeight: '100vh',
  background: '#200043',
  position: 'relative',
  color: '#ffffff',
  width: '100%',
  maxWidth: '100%',
  // `clip` and not `hidden`. Both stop sideways overflow, but `hidden` turns this element
  // into a scroll container, and a scroll container ancestor is what a descendant's
  // `position: sticky` sticks to. Since this box is exactly as tall as its content there is
  // nothing to stick within, so every sticky header under it silently scrolled away (the
  // student dashboard header). `clip` clips without creating that scroll container.
  overflowX: 'clip',
  boxSizing: 'border-box'
};

export const contentWrapperStyle = {
  position: 'relative',
  zIndex: 1,
  width: '100%',
  maxWidth: '100%',
  // Same reason as outerContainerStyle above: clip sideways, do not become a scroll container.
  overflowX: 'clip'
};

export const sidebarStyle = {
  width: 220,
  padding: '24px 16px',
  borderRight: '1px solid rgba(255,255,255,0.1)'
};

export const sidebarTextStyle = {
  fontFamily: 'var(--cs-font)',
  fontSize: 18,
  color: '#ffffff'
};

// Surfaces. One set of names for "what is this box sitting on", because the same job was
// being done by six different hand-picked hexes across the app (#1a1033, #160a2c, #1a0b34,
// #2a1156, #241048, #1b0f38), which is why modals and fields never quite matched each other.
//
//   page    the app ground, identical to outerContainerStyle's background
//   raised  a solid card or modal sitting on the page
//   sunken  an input or well, darker than the card it sits in
//   popover a tooltip or menu floating above everything
export const surfaces = {
  page: '#200043',
  raised: '#2a1156',
  sunken: '#1b0f38',
  popover: '#1a0b34',
  border: 'rgba(255, 255, 255, 0.18)',
  borderStrong: 'rgba(139, 92, 246, 0.5)',
};

// A modal or solid card. Unlike panelStyle this is opaque: over a starfield a translucent
// panel lets stars read through the content underneath it.
export const modalSurface = {
  background: surfaces.raised,
  border: `1px solid ${surfaces.border}`,
  borderRadius: 16,
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.40), 0 0 0 1px rgba(139, 92, 246, 0.14)',
};

// A floating tooltip or autocomplete menu.
export const popoverSurface = {
  background: surfaces.popover,
  border: `1px solid ${surfaces.borderStrong}`,
  borderRadius: 12,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
};

export const panelStyle = {
  background: 'rgba(255,255,255,0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.3)',
  borderRadius: 16,
  // Soft dark drop shadow + a faint purple ring for depth. Replaces the old harsh white
  // glow (0 0 20/40px rgba(255,255,255,.4/.2)) that haloed small cards (e.g. the gateway
  // sections) and read as dated. Mirrors panelStyleLight's depth-without-halo.
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.32), 0 0 0 1px rgba(139, 92, 246, 0.10)',
  padding: 20,
  marginBottom: 16
};

// LIGHT-mode panel — a designed CareerStar light card on the SAME dark starfield (not a
// full light theme). Soft PURPLE-TINTED off-white (not clinical white), real depth (a
// drop shadow + faint purple glow + a purple-tinted border), so it has dimension against
// the stars. Interior text flips dark-on-light and headers go brand-purple via the
// .cs-panel-light rules in index.css; accent-colored text + data-viz keep their color.
export const panelStyleLight = {
  background: 'linear-gradient(150deg, rgba(247,244,255,0.97), rgba(236,231,251,0.95))',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(139,92,246,0.28)',
  borderRadius: 16,
  boxShadow: '0 10px 28px rgba(60,40,120,0.22), 0 1px 0 rgba(255,255,255,0.7) inset, 0 0 0 1px rgba(139,92,246,0.05)',
  padding: 20,
  marginBottom: 16,
  color: '#2a2350'
};

// Opaque variants, for a dialog. A modal sits over a dimmed overlay, so a translucent panel
// shows the page and the overlay through the dialog's own content; that is what made the
// gateway's Manage section box read as a see-through black-and-blue card. Same border, radius
// and shadow as the panel it matches, so a dialog is still the same material.
export const panelSolidStyle = {
  ...panelStyle,
  background: surfaces.raised,
  backdropFilter: 'none',
};
export const panelSolidStyleLight = {
  ...panelStyleLight,
  background: '#f7f4ff',
  backdropFilter: 'none',
};


// Modal / popup card. Derived from panelStyle so a dialog reads as the SAME
// material as every other surface, just lifted. Before this existed each modal
// hardcoded its own dark hex (#1a1033, #1a1a2e, #161628, rgba(40,...)) and they
// had all drifted apart. Pair it with modalOverlayStyle.
export const modalCardStyle = {
  ...panelStyle,
  // A touch more opaque than a flat panel: a dialog sits above the page, and the
  // starfield behind it should not read through the text.
  background: 'rgba(255,255,255,0.13)',
  backdropFilter: 'blur(22px)',
  boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.16)',
  padding: 24,
  marginBottom: 0,
  textAlign: 'left',
  color: '#FFFFFF',
  fontFamily: 'var(--cs-font)',
};

export const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  background: 'rgba(14, 5, 30, 0.68)',
  backdropFilter: 'blur(2px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  boxSizing: 'border-box',
};

export const statValueStyle = {
  fontSize: 28,
  fontWeight: 'bold',
  color: '#ffffff'
};

export const statLabelStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.85)'
};

// Accent color presets per dashboard type
export const accentColors = {
  student: {
    gradient: 'linear-gradient(90deg, #8B5CF6, #EC4899)',
    highlight: 'rgba(139, 92, 246, 0.3)',
    icon: '#8B5CF6'
  },
  manager: {
    gradient: 'linear-gradient(90deg, #22c55e, #3b82f6)',
    highlight: 'rgba(34, 197, 94, 0.3)',
    icon: '#22c55e'
  },
  admin: {
    gradient: 'linear-gradient(90deg, #EC4899, #8B5CF6)',
    highlight: 'rgba(236, 72, 153, 0.3)',
    icon: '#EC4899'
  },
  faculty: {
    gradient: '#8B5CF6',
    highlight: 'rgba(139, 92, 246, 0.3)',
    icon: '#8B5CF6'
  }
};

// Named tokens for the palette/gradients/fonts ALREADY used inline across the app — no NEW
// values, this just centralizes the existing hexes/gradients so the ui/ primitives reference
// tokens instead of raw literals. This step reduces the value-set; it never adds to it.
export const colors = {
  bg: '#200043',
  purple: '#8B5CF6',
  purpleDeep: '#7c3aed',
  pink: '#EC4899',
  green: '#22c55e',
  greenDeep: '#16a34a',
  lilac: '#c4b5fd',
  white: '#FFFFFF',
  ink: '#000000',
  cardBorder: '#e0e0e0',
  cardShadow: '#dcd9de',
  cardTitle: '#0D062D',
  mediaContain: '#1c0738',
  // Feedback colors. These were being retyped as raw hex at every call site
  // (#86efac / #fca5a5 / #fbbf24 appear dozens of times across the faculty view),
  // which is why success and error messages had drifted apart in shade.
  success: '#86efac',
  danger: '#fca5a5',
  warning: '#fbbf24',
  // Common text tints, so "white but softer" stops being a magic rgba each time.
  textSoft: 'rgba(255,255,255,0.75)',
  textMuted: 'rgba(255,255,255,0.55)',
  textFaint: 'rgba(255,255,255,0.45)',
};
export const gradients = {
  primary: 'linear-gradient(135deg, #8B5CF6, #7c3aed)',
  accent: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
  success: 'linear-gradient(135deg, #22c55e, #16a34a)',
};
// One face for the product. head/body are kept as separate names because ~80 call sites
// already read them; hierarchy now comes from weight and size, not from a second family.
export const fonts = { head: 'var(--cs-font)', body: 'var(--cs-font)' };

// Corner radii, named. These are the values the app already converged on, not a new set:
// 8 was by far the commonest, 12 the usual container, and 16 is what panelStyle uses. The
// 18 near-duplicates (5, 6, 7, 9, 10, 11, 14, 15, 20 and two spellings of the pill) were
// collapsed into these. `xs` stays for chips and inline tags that are too short to carry 8.
// Dots and thin bars keep their own 2-3px radius because there the number is making a
// circle, not expressing a style.
export const radii = { xs: 4, sm: 8, md: 12, lg: 16, pill: 999, circle: '50%' };
